import * as React from "react";
import Progress from "./Progress";
import { AgendaAppProps, AgendaAppState } from "../models";
import { DialogBox } from "./DialogBox";
import { Icon } from "office-ui-fabric-react/lib/Icon";
import { ImageFit } from "office-ui-fabric-react/lib/Image";
import { Link } from "office-ui-fabric-react/lib/Link";
import { Separator } from "office-ui-fabric-react/lib/Separator";
import { getAgendaList, startAgenda, uploadFile } from "../server";
import { getErrorMessage, getWordFile, getExcelFile } from "../common";

/* global console, Office, window */
const iconImageProps = (item) => ({
  src: item.icon,
  alt: "agenda",
  imageFit: ImageFit.contain,
  width: 40,
  height: 40,
});

export default class AgendaApp extends React.Component<AgendaAppProps, AgendaAppState> {
  constructor(props, context) {
    super(props, context);
    this.state = {
      listItems: [],
      isLoading: true,
      currentItemData: {},
      dialogProps: { hidden: true },
    };
  }

  fetchAgendaList = async () => {
    const { token, strings } = this.props;
    try {
      const agendaResponse = await getAgendaList(token).then((r) => r.json());
      if (agendaResponse?.message === "The authorization token is missing") {
        this.setState({
          dialogProps: {
            hidden: false,
            title: strings.dialogErrorTitle,
            subTitle: strings.noAccessToSofa,
          },
        });
      }
      return agendaResponse.data.processes.agendas;
    } catch (e) {
      console.error(e);
      this.setState({
        dialogProps: {
          hidden: false,
          title: strings.dialogErrorTitle,
          subTitle: strings.noAccessToSofa,
        },
      });
      return [];
    }
  };

  componentDidMount() {
    if (this.state.listItems.length <= 0) {
      this.fetchAgendaList().then((list) => {
        this.setState({
          isLoading: false,
          listItems: list,
        });
      });
    }
  }

  getFileId = async (file: Blob, fileName: string) => {
    const { token, strings } = this.props;
    try {
      const uploadedResponse = await uploadFile(token, file, fileName).then((r) => r.json());
      if (uploadedResponse?.message === "The authorization token is missing") {
        this.setState({
          dialogProps: {
            hidden: false,
            title: strings.dialogErrorTitle,
            subTitle: strings.noAccessToSofa,
          },
        });
      }
      if (uploadedResponse.fileId) {
        return [{ id: uploadedResponse.fileId, name: fileName }];
      } else {
        this.setState({
          dialogProps: {
            hidden: false,
            title: strings.dialogErrorTitle,
            subTitle: strings.noAccessToSofa,
          },
        });
        return [];
      }
    } catch (e) {
      console.error(e);
      this.setState({
        dialogProps: {
          hidden: false,
          title: strings.dialogErrorTitle,
          subTitle: strings.noAccessToSofa,
        },
      });
      return [];
    }
  };

  showSuccessDialog = () => {
    const { strings } = this.props;
    this.setState({
      dialogProps: {
        hidden: false,
        title: strings.dialogSuccessTitle,
        subTitle: strings.agendaSuccessfullyStarted,
      },
      isLoading: false,
    });
  };

  showErrorDialog = (errorResponse: any) => {
    const { strings } = this.props;
    const errorMessage = getErrorMessage(errorResponse);
    this.setState({
      dialogProps: {
        hidden: false,
        title: strings.dialogErrorTitle,
        subTitle: errorMessage,
      },
      isLoading: false,
    });
  };

  showWarningPopupDialog = (agendaUrl: string) => {
    const { strings } = this.props;
    this.setState({
      dialogProps: {
        hidden: false,
        title: strings.dialogPopUpWarning,
        agendaUrl: agendaUrl,
      },
    });
  };

  handleSuccessResponse = (agendaUrl: string) => {
    Office.context.ui.displayDialogAsync(
      `${process.env.REACT_APP_APP_DOMAIN}iframe.html?finalTarget=` + agendaUrl,
      { height: 90, width: 50, displayInIframe: false, promptBeforeOpen: true },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Error: " + asyncResult.error.message);
          if (asyncResult.error.name === "DialogBlocked") {
            this.showWarningPopupDialog(agendaUrl);
          } else {
            this.showErrorDialog(asyncResult.error.message);
          }
        } else {
          this.showSuccessDialog();
        }
      }
    );
  };

  handleSubmitWorldFile = async (file: Blob, fileName: string) => {
    const { token, strings } = this.props;
    // Upload file and get id
    try {
      const uploadedFileData = await this.getFileId(file, fileName);
      // Start Agenda
      const messageIdent = "BA4BF472-79B8-4427-A35F-966509C561C6";
      const agendaId = this.state.currentItemData.tdId;
      const startAgendaResponse = await startAgenda(token, agendaId, [uploadedFileData[0].id], messageIdent).then(
        (response) => response.json()
      );
      if (startAgendaResponse?.message === "The authorization token is missing") {
        this.setState({
          dialogProps: {
            hidden: false,
            title: strings.dialogErrorTitle,
            subTitle: strings.noAccessToSofa,
          },
          isLoading: false,
        });
      }
      if (startAgendaResponse?.data?.processes?.sendMessageToAgendaEx?.url) {
        const agendaUrl = startAgendaResponse.data.processes.sendMessageToAgendaEx.url;
        this.handleSuccessResponse(agendaUrl);
      } else {
        this.showErrorDialog(startAgendaResponse);
      }
    } catch (e) {
      console.error(e);
    }
  };

  onStartAgenda = async (item: any) => {
    this.setState({ isLoading: true, currentItemData: item });
    if (Office.context.diagnostics.host === Office.HostType.Word) {
      await getWordFile(this.handleSubmitWorldFile);
    }
    if (Office.context.diagnostics.host === Office.HostType.Excel) {
      await getExcelFile(this.handleSubmitWorldFile);
    }
  };

  handleHideDialog = (agendaUrl?: string) => {
    this.setState({ dialogProps: { hidden: true } });
    if (!!agendaUrl && agendaUrl.length > 0) {
      window.open(agendaUrl);
    }
    Office.addin.hide();
  };

  render() {
    const { isOfficeInitialized, strings } = this.props;
    const { listItems, isLoading, dialogProps } = this.state;
    if (!isOfficeInitialized) {
      return <div className={"ms-Error__text"}>{strings.initializedErrorMessage}</div>;
    }
    return (
      <div className={"container"}>
        {isLoading ? (
          <Progress />
        ) : (
          <div className={"agendaList"}>
            <p className={"agendaList__title"}>{strings.agendaTitle}</p>
            {!dialogProps.hidden && (
              <DialogBox
                title={dialogProps.title}
                subTitle={dialogProps.subTitle}
                hideDialog={dialogProps.hidden}
                toggleHideDialog={this.handleHideDialog}
                agendaUrl={dialogProps.agendaUrl}
              />
            )}
            {listItems.map((item, id) => (
              <div key={"agendaListItem" + id}>
                <div className={"agendaList__item"}>
                  <Icon imageProps={iconImageProps(item)} className={"agendaItem__icon"} />
                  <Link onClick={() => this.onStartAgenda(item)}>{item.displayName}</Link>
                </div>
                <Separator />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
